import { ReactNode } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import cn from 'clsx'

// Components
import { Button, IconCheckmarkShadow, IconExclamation, IconClock } from '@tracklib/kit'

import styles from './paymentstatus.css'

const spring = {
  type: 'spring',
  stiffness: 200,
  damping: 20,
  mass: 1,
  velocity: 0,
}
const enter = {
  opacity: 1,
  y: 0,
}
const exit = {
  opacity: 0,
  y: 150,
}

type SuccessBoxProps = {
  title?: string
  description?: ReactNode
  buttonTo?: string
  buttonText?: string
  iconType?: 'success' | 'warning' | 'waiting'
  buttonExternal?: boolean
  datatesting?: string
  buttonDatatesting?: string
  auxiliary?: ReactNode
}

export const SuccessBox = ({
  title,
  description,
  buttonTo,
  buttonText,
  iconType,
  buttonExternal,
  datatesting,
  buttonDatatesting,
  auxiliary,
}: SuccessBoxProps) => {
  return (
    <div className={styles.completedSection} datatesting={datatesting}>
      {iconType === 'success' && <IconCheckmarkShadow className={styles.icon} />}
      {iconType === 'warning' && <IconExclamation className={styles.exclamationIcon} />}
      {iconType === 'waiting' && <IconClock className={styles.clockIcon} />}
      <h3 className={styles.title}>{title}</h3>
      {description && <p>{description}</p>}
      {auxiliary}
      {buttonTo && buttonText && (
        <>
          {buttonExternal ? (
            <Button href={buttonTo} target="_blank" datatesting={buttonDatatesting}>
              {buttonText}
            </Button>
          ) : (
            <Button to={buttonTo} datatesting={buttonDatatesting}>
              {buttonText}
            </Button>
          )}
        </>
      )}
    </div>
  )
}

type PaymentStatusProps = {
  lowerComponent?: ReactNode
  title?: string
  description?: ReactNode
  iconType?: 'success' | 'warning' | 'waiting'
  animationKey?: string
  datatesting?: string
  toast?: ReactNode
}

const PaymentStatus = (props: PaymentStatusProps) => {
  const { lowerComponent, title, description, iconType, animationKey = 'jkajksdkj2jdj2', datatesting, toast } = props
  return (
    <AnimatePresence>
      <motion.div
        animate={enter}
        exit={exit}
        initial={exit}
        transition={spring}
        key={animationKey}
        className={cn(toast ? null : styles.box)}
      >
        {toast}
        <SuccessBox title={title} description={description} iconType={iconType} datatesting={datatesting} />
        {lowerComponent}
      </motion.div>
    </AnimatePresence>
  )
}

export default PaymentStatus

interface PaymentSuccessProps {
  title?: string
  button?: ReactNode
}

export const PaymentSuccess = ({ title, button }: PaymentSuccessProps) => {
  return (
    <PaymentStatus
      title={title || 'Payment successful!'}
      iconType="success"
      datatesting="payment_successful"
      lowerComponent={
        <div className={styles.tryAgainButton} datatesting="payment_error">
          {button}
        </div>
      }
    />
  )
}

interface PaymentErrorButton {
  button?: ReactNode
}

export const PaymentError = ({ button }: PaymentErrorButton) => {
  return (
    <PaymentStatus
      title="Something went wrong"
      description="We are not sure what, please contact support or go back and try again."
      iconType="warning"
      lowerComponent={
        <div className={styles.tryAgainButton} datatesting="payment_error">
          {button}
        </div>
      }
    />
  )
}
